import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";
import LeagueRoundRobinView from "./league/LeagueRoundRobinView";
import LeagueSumatoriaJuegosView from "./league/LeagueSumatoriaJuegosView";
import LeagueEscaleraView from "./league/LeagueEscaleraView";
import EventRegistrationForm from "./EventRegistrationForm";
import PendingStateCard from "./PendingStateCard";
import PendingMatchCard from "./PendingMatchCard";
import PendingRankingTable from "./PendingRankingTable";
import { Calendar, MapPin, Users, Clock, Trophy, ArrowLeft, User, Share2, MessageCircle } from "lucide-react";
import { useEvents } from "@/hooks/useEvents";
import { getTournamentById } from "@/utils/eventUtils";
import { useUserRegistration } from "@/hooks/useUserRegistration";
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from "sonner";
import heroPadel from "@/assets/hero-padel.jpg";
import heroTennis from "@/assets/hero-tennis.jpg";
import heroPickleball from "@/assets/hero-pickleball.jpg";
interface LeagueDetailsProps {
  leagueId: string;
  onBack: () => void;
  showRegistration?: boolean;
  onContactOrganizer?: () => void;
}

const parseTennisCategories = (rules: any): string | null => {
  if (!rules) return null;
  try {
    const rulesData = typeof rules === 'string' ? JSON.parse(rules) : rules;
    const tc = rulesData?.ladderConfig?.tennisCategories;
    if (tc?.selectedGenders?.length > 0 && tc?.subcategoryDetails) {
      const parts: string[] = [];
      tc.selectedGenders.forEach((gender: string) => {
        const details = tc.subcategoryDetails[gender];
        if (Array.isArray(details) && details.length > 0) {
          details.forEach((d: any) => parts.push(`${gender} ${d.name || d}`));
        } else {
          parts.push(gender);
        }
      });
      if (parts.length > 0) return parts.join(', ');
    }
  } catch (e) { /* ignore */ }
  return null;
};

const LeagueDetails = ({ leagueId, onBack, showRegistration = false, onContactOrganizer }: LeagueDetailsProps) => {
  const { registration, loading: registrationLoading } = useUserRegistration(leagueId);
  const { events, loading: eventsLoading } = useEvents();
  const [showRegistrationForm, setShowRegistrationForm] = useState(showRegistration);
  
  // Handler para iniciar proceso de inscripción
  const handleRegisterClick = () => {
    setShowRegistrationForm(true);
    setTimeout(() => {
      const registrationElement = document.getElementById('registration-section');
      if (registrationElement) {
        registrationElement.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };
  
  // Wait for events to load before rendering
  if (eventsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Cargando...</p>
        </div>
      </div>
    );
  }
  
  // Get league data from mock data (since the real Supabase data structure might not match)
  const mockLeagues = [
    {
      id: 10,
      title: "Liga Pádel Round Robin - Copa de Verano",
      organizer: "Club Deportivo Central",
      sport: "Pádel",
      startDate: "2025-07-20",
      endDate: "2025-09-02",
      location: "Club Deportivo Central",
      category: "Mixtos",
      subcategory: "A",
      status: "active",
      matchType: "doubles",
      leagueFormat: "round-robin",
      partner: "Carmen Silva",
      currentStage: "Jornada 8 de 15"
    },
    {
      id: 11,
      title: "Liga Tenis Sumatoria de Juegos",
      organizer: "Tennis Club Elite",
      sport: "Tenis",
      startDate: "2025-07-21",
      endDate: "2025-09-10",
      location: "Tennis Club Elite",
      category: "Masculina",
      subcategory: "A",
      status: "active",
      matchType: "singles",
      leagueFormat: "sumatoria-juegos",
      currentStage: "Temporada Regular - 145 juegos acumulados"
    },
    {
      id: 18,
      title: "Liga Escalera Pádel Singles",
      organizer: "Club Deportivo Central",
      sport: "Pádel",
      startDate: "2025-07-22",
      endDate: "2025-09-20",
      location: "Club Deportivo Central",
      category: "Masculina",
      subcategory: "2nda",
      status: "active",
      matchType: "singles",
      leagueFormat: "escalera-piramidal",
      currentStage: "Jornada 5 de 12"
    },
    {
      id: 19,
      title: "Liga Escalera Pickleball Dobles",
      organizer: "Club Pickleball Activo",
      sport: "Pickleball",
      startDate: "2025-07-23",
      endDate: "2025-09-20",
      location: "Centro Pickleball Activo",
      category: "Mixtos",
      subcategory: "A",
      status: "active",
      matchType: "doubles",
      leagueFormat: "escalera-piramidal",
      partner: "María Torres",
      currentStage: "Semana 4 de 8"
    },
    {
      id: 20,
      title: "Liga Tenis Próxima - Temporada Otoño",
      organizer: "Tennis Club Premium",
      sport: "Tenis",
      startDate: "2025-08-15",
      endDate: "2025-10-15",
      location: "Tennis Club Premium",
      category: "Mixtos",
      subcategory: "A",
      status: "upcoming",
      matchType: "doubles",
      leagueFormat: "round-robin",
      partner: "Ana López",
      currentStage: "Próximo a iniciar"
    },
    {
      id: 23,
      title: "Liga Pádel Round Robin - Temporada Invierno",
      organizer: "Club Deportivo Norte",
      sport: "Pádel",
      startDate: "2025-09-01",
      endDate: "2025-11-01",
      location: "Club Deportivo Norte",
      category: "Mixtos",
      subcategory: "A",
      status: "upcoming",
      matchType: "doubles",
      leagueFormat: "round-robin",
      partner: "María González",
      currentStage: "Próximo a iniciar"
    },
    {
      id: 24,
      title: "Liga Tenis Sumatoria - Temporada Invierno",
      organizer: "Tennis Club Premium",
      sport: "Tenis",
      startDate: "2025-09-15",
      endDate: "2025-11-15",
      location: "Tennis Club Premium",
      category: "Masculina",
      subcategory: "A",
      status: "upcoming",
      matchType: "singles",
      leagueFormat: "sumatoria-juegos",
      currentStage: "Próximo a iniciar"
    },
    {
      id: 25,
      title: "Liga Escalera Pickleball - Temporada Invierno",
      organizer: "Club Pickleball Elite",
      sport: "Pickleball",
      startDate: "2025-10-01",
      endDate: "2025-12-01",
      location: "Club Pickleball Elite",
      category: "Mixtos",
      subcategory: "A",
      status: "upcoming",
      matchType: "doubles",
      leagueFormat: "escalera-piramidal",
      partner: "Ana López",
      currentStage: "Próximo a iniciar"
    },
    // Mock finished leagues for testing
    {
      id: 26,
      title: "Liga Round Robin Finalizada",
      organizer: "Club Las Raquetas",
      sport: "Pádel",
      date: "2024-12-01",
      endDate: "2024-12-22",
      location: "Club Las Raquetas",
      leagueFormat: "round-robin",
      gameType: "doubles",
      category: "mixtos",
      subcategory: "A",
      weeksRemaining: 0,
      totalWeeks: 8,
      status: 'finished' as const
    },
    {
      id: 27,
      title: "Liga Sumatoria Finalizada", 
      organizer: "Club Las Raquetas",
      sport: "Pádel",
      date: "2024-11-01",
      endDate: "2024-11-29",
      location: "Club Las Raquetas",
      leagueFormat: "sumatoria-juegos",
      gameType: "doubles",
      category: "mixtos",
      subcategory: "A",
      weeksRemaining: 0,
      totalWeeks: 8,
      status: 'finished' as const
    },
    {
      id: 28,
      title: "Liga Escalera Finalizada",
      organizer: "Club Las Raquetas", 
      sport: "Pádel",
      date: "2024-10-01",
      endDate: "2024-10-26",
      location: "Club Las Raquetas",
      leagueFormat: "escalera-piramidal",
      gameType: "doubles",
      category: "mixtos",
      subcategory: "A",
      weeksRemaining: 0,
      totalWeeks: 8,
      status: 'finished' as const
    }
  ];
  
  // Helper function to format category display
  const formatCategoryDisplay = (category: string | null, subcategory: string | null): string => {
    if (!category) return "Mixto A";
    if (!subcategory || subcategory === category) return category;
    return `${category} ${subcategory}`;
  };

  // Interface for league data
  interface LeagueData {
    id: string | number;
    title: string;
    organizer: string;
    sport: string;
    startDate: string;
    endDate?: string;
    location: string;
    category: string;
    subcategory?: string;
    status: 'upcoming' | 'active' | 'finished' | 'cancelled' | 'completed' | 'ongoing';
    matchType?: string;
    leagueFormat?: string;
    start_time?: string;
    end_time?: string;
    game_days?: string[];
    partner?: string;
    currentStage: string;
    isLeague?: boolean;
    total_weeks?: number;
    matches_per_week?: number;
    image_url?: string;
  }

  // Find the league by ID - first check real events from Supabase
  const realLeague = events.find(e => e.id === leagueId);
  
  // DEBUG: Log para ver qué datos tiene realLeague
  console.log('🔍 DEBUG realLeague:', {
    id: realLeague?.id,
    title: realLeague?.title,
    start_time: realLeague?.start_time,
    end_time: realLeague?.end_time,
    game_days: realLeague?.game_days,
    full_object: realLeague
  });
  
  // Normalize sport: remove accents (pádel -> padel, tenis -> tennis)
  const normalizeSport = (sport: string): 'padel' | 'tennis' | 'pickleball' => {
    const normalized = sport.toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, ''); // Remove diacritics/accents
    
    switch (normalized) {
      case 'padel':
        return 'padel';
      case 'tenis':
      case 'tennis':
        return 'tennis';
      case 'pickleball':
        return 'pickleball';
      default:
        return 'padel';
    }
  };
  
  // Normalize league format: database uses underscores, code expects hyphens
  // Also map abbreviated values to their full expected names
  const normalizeLeagueFormat = (format: string | undefined): string => {
    if (!format) return 'round-robin';
    const normalized = format.toLowerCase().replace(/_/g, '-');
    
    // Map DB values to expected frontend values
    switch (normalized) {
      case 'escalera':
      case 'escalera-piramidal':
        return 'escalera-piramidal';
      case 'sumatoria':
      case 'sumatoria-juegos':
        return 'sumatoria-juegos';
      case 'round-robin':
      default:
        return normalized;
    }
  };
  
  // Normalize event status from DB to frontend expected values
  const normalizeStatus = (status: string): 'upcoming' | 'active' | 'finished' => {
    switch (status) {
      case 'ongoing':
        return 'active';
      case 'completed':
      case 'cancelled':
        return 'finished';
      case 'upcoming':
        return 'upcoming';
      case 'active':
        return 'active';
      case 'finished':
        return 'finished';
      default:
        return 'active';
    }
  };

  const league: LeagueData = realLeague ? {
    id: realLeague.id,
    title: realLeague.title,
    organizer: realLeague.organizer_company_name || "Club Las Raquetas",
    sport: realLeague.sport,
    startDate: realLeague.start_date,
    endDate: realLeague.end_date || realLeague.start_date,
    location: realLeague.location,
    // Use parsed genderCategories + subcategories when available (tennis leagues)
    category: (() => {
      // 1. Check if useEvents already parsed categories
      if (realLeague.genderCategories?.length > 0 && realLeague.subcategories 
          && Object.keys(realLeague.subcategories).length > 0) {
        const parts: string[] = [];
        realLeague.genderCategories.forEach((gender: string) => {
          const subs = realLeague.subcategories?.[gender];
          if (subs && subs.length > 0) {
            subs.forEach((sub: string) => parts.push(`${gender} ${sub}`));
          } else {
            parts.push(gender);
          }
        });
        return parts.join(', ');
      }
      
      // 2. Direct fallback: parse tennisCategories from raw rules JSON
      if (realLeague.rules) {
        try {
          const rulesData = typeof realLeague.rules === 'string' 
            ? JSON.parse(realLeague.rules) : realLeague.rules;
          const tc = rulesData?.ladderConfig?.tennisCategories;
          if (tc?.selectedGenders?.length > 0 && tc?.subcategoryDetails) {
            const parts: string[] = [];
            tc.selectedGenders.forEach((gender: string) => {
              const details = tc.subcategoryDetails[gender];
              if (Array.isArray(details) && details.length > 0) {
                details.forEach((d: any) => parts.push(`${gender} ${d.name || d}`));
              } else {
                parts.push(gender);
              }
            });
            if (parts.length > 0) return parts.join(', ');
          }
        } catch (e) { /* ignore */ }
      }
      
      // 3. Final fallback
      return formatCategoryDisplay(
        realLeague.gender_category || realLeague.category || null, 
        realLeague.category || realLeague.subcategory || null
      );
    })(),
    subcategory: parseTennisCategories(realLeague.rules) || realLeague.subcategory || realLeague.category,
    status: normalizeStatus(realLeague.status),
    matchType: realLeague.match_type,
    // Normalize format from underscore to hyphen
    leagueFormat: normalizeLeagueFormat(realLeague.league_format),
    start_time: realLeague.start_time,
    end_time: realLeague.end_time,
    game_days: realLeague.game_days,
    partner: registration?.partner_name,
    currentStage: realLeague.status === 'upcoming' ? 'Próximo a iniciar' : 'Temporada en curso',
    total_weeks: realLeague.total_weeks,
    matches_per_week: realLeague.matches_per_week,
    image_url: realLeague.image_url
  } : mockLeagues.find(l => l.id === parseInt(leagueId) || l.id.toString() === leagueId) as LeagueData || {
    id: leagueId,
    title: "Liga Pádel Primavera",
    organizer: "Club Las Raquetas",
    sport: "Pádel",
    startDate: "2025-06-15",
    endDate: "2025-08-15",
    location: "Club Las Raquetas",
    category: formatCategoryDisplay(registration?.category || null, registration?.subcategory || null) || "Mixto A",
    subcategory: registration?.subcategory || "Mixto",
    partner: registration?.partner_name || "María González",
    status: "active" as const,
    matchType: "doubles" as const,
    currentStage: "Semana 5",
    leagueFormat: "round-robin" as const,
    start_time: undefined,
    end_time: undefined,
    game_days: undefined,
    isLeague: true
  };

  const getSportColor = (sport: string) => {
    switch (sport.toLowerCase()) {
      case 'pádel':
        return 'bg-emerald-100 text-emerald-800';
      case 'tenis':
        return 'bg-green-100 text-green-800';
      case 'pickleball':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    // Parse date as local to avoid UTC offset issues with YYYY-MM-DD format
    let date: Date;
    if (dateString.includes('T')) {
      date = new Date(dateString);
    } else {
      const [year, month, day] = dateString.split('-').map(Number);
      date = new Date(year, month - 1, day);
    }
    return date.toLocaleDateString('es-ES', { 
      day: 'numeric',
      month: 'short'
    });
  };

  const getLeagueFormatDisplay = () => {
    // Format is already normalized to use hyphens
    const format = league.leagueFormat || 'round-robin';
    switch (format) {
      case 'round-robin':
        return 'Round Robin';
      case 'sumatoria-juegos':
        return 'Sumatoria de Juegos';
      case 'escalera-piramidal':
        return 'Escalera Piramidal';
      default:
        return 'Round Robin';
    }
  };

  // Get organizer image or fallback to sport-based hero image
  const getOrganizerImage = (organizerName: string, sport: string, imageUrl?: string): string => {
    if (imageUrl) return imageUrl;
    // Fallback to sport-specific hero images
    const normalizedSport = sport.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    switch (normalizedSport) {
      case 'padel':
        return heroPadel;
      case 'tenis':
      case 'tennis':
        return heroTennis;
      case 'pickleball':
        return heroPickleball;
      default:
        return heroPadel;
    }
  };

  // Handle share functionality
  const handleShare = async () => {
    const shareData = {
      title: league.title,
      text: `¡Mira esta liga de ${league.sport}! ${league.title} en ${league.location}`,
      url: window.location.href,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success("Enlace copiado al portapapeles");
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  const renderLeagueView = () => {
  const leagueData = {
    id: league.id.toString(),
    name: league.title,
    date: league.startDate,
    endDate: league.endDate,
    club: league.location,
    // Use already normalized format from league object
    modality: (league.leagueFormat || 'round-robin') as 'round-robin' | 'escalera-piramidal' | 'sumatoria-juegos',
    gameType: (league.matchType === 'dobles' || league.matchType === 'doubles') ? 'doubles' as const : 'singles' as const,
    sport: normalizeSport(league.sport),
    category: (() => {
      const fromLeague = league.category;
      if (fromLeague && fromLeague !== 'mixto' && fromLeague !== 'mixtos') {
        return fromLeague;
      }
      return parseTennisCategories(realLeague?.rules) || fromLeague || 'mixtos';
    })() as any,
    subcategory: (() => {
      const parsed = parseTennisCategories(realLeague?.rules);
      if (parsed) return parsed;
      return league.subcategory || league.category || '';
    })(),
    partner: league.partner,
    currentStage: league.currentStage,
    status: league.status as 'upcoming' | 'active' | 'finished',
    start_time: league.start_time,
    end_time: league.end_time,
    game_days: league.game_days,
    total_weeks: league.total_weeks || 8,
    matches_per_week: league.matches_per_week || 3,
    organizer: league.organizer,
    // Ladder league specific - parse ladderConfig from rules JSON
    ...(() => {
      const rulesData = (() => {
        try {
          return typeof realLeague?.rules === 'string' 
            ? JSON.parse(realLeague.rules) : realLeague?.rules;
        } catch { return null; }
      })();

      const parsedLadderConfig = rulesData?.ladderConfig || null;
      const parsedRoundRobinConfig = rulesData?.roundRobinConfig || null;

      const parsedRestWeeks = 
        (rulesData?.ladderConfig?.restWeeks as number[]) 
        || (rulesData?.restWeeks as number[]) 
        || [];

      // Fallback: si ladderConfig no tiene winnerType, usar roundRobinConfig
      const winnerType = parsedLadderConfig?.winnerType 
        || parsedRoundRobinConfig?.winnerType 
        || 'first-place';
      const eliminationPhase = parsedLadderConfig?.eliminationPhase 
        || parsedRoundRobinConfig?.eliminationPhase;
      const hasLadderEliminationConfig = Boolean(
        parsedLadderConfig?.eliminationConfig?.singleDateSchedule?.date ||
        Object.keys(parsedLadderConfig?.eliminationConfig?.phaseSchedules || {}).length > 0
      );
      const eliminationConfig = hasLadderEliminationConfig
        ? parsedLadderConfig.eliminationConfig
        : parsedRoundRobinConfig?.eliminationConfig;

      return {
        winnerType: winnerType as 'first-place' | 'elimination',
        challengeType: parsedLadderConfig?.challengeType || 'simple' as 'simple' | 'double',
        eliminationPhase: eliminationPhase as 'octavos' | 'cuartos' | 'semifinal' | 'final' | undefined,
        eliminationConfig,
        restWeeks: parsedRestWeeks,
      };
    })()
  };

    // Check if league is upcoming and should show pending states
    const isUpcoming = league.status === 'upcoming';
    // Format is already normalized in league object
    const format = league.leagueFormat || 'round-robin';
    
    // Debug log to help identify the issue
    console.log('League format (normalized):', format, 'for league:', league.title);
    console.log('League status:', league.status, 'isUpcoming:', isUpcoming);
    
    // Show full league views for both active and upcoming leagues

    // Show active/finished league views
    let view;
    
    switch (format) {
      case 'round-robin':
        view = <LeagueRoundRobinView 
          league={leagueData} 
          leagueMatches={null} 
          onContactOrganizer={onContactOrganizer}
          onRegisterClick={handleRegisterClick}
          isUserRegistered={!!registration}
          registrationLoading={registrationLoading}
        />;
        break;
      case 'sumatoria-juegos':
        view = <LeagueSumatoriaJuegosView 
          league={leagueData} 
          leagueMatches={null} 
          onContactOrganizer={onContactOrganizer}
          onRegisterClick={handleRegisterClick}
          isUserRegistered={!!registration}
          registrationLoading={registrationLoading}
        />;
        break;
      case 'escalera-piramidal':
        view = <LeagueEscaleraView 
          league={leagueData} 
          leagueMatches={null} 
          onContactOrganizer={onContactOrganizer}
          onRegisterClick={handleRegisterClick}
          isUserRegistered={!!registration}
          registrationLoading={registrationLoading}
        />;
        break;
      default:
        view = <LeagueRoundRobinView 
          league={leagueData} 
          leagueMatches={null} 
          onContactOrganizer={onContactOrganizer}
          onRegisterClick={handleRegisterClick}
          isUserRegistered={!!registration}
          registrationLoading={registrationLoading}
        />;
    }

    return view;
  };

  // Prepare event data for registration form
  const eventForRegistration = {
    id: typeof league.id === 'string' ? league.id : String(league.id),
    title: league.title,
    organizer: league.organizer,
    sport: league.sport,
    date: league.startDate,
    endDate: league.endDate,
    // Ensure we always use the correct time from Supabase
    time: (realLeague?.start_time || league.start_time) ? (realLeague?.start_time || league.start_time).slice(0, 5) : "09:00",
    start_time: realLeague?.start_time || league.start_time,
    end_time: realLeague?.end_time || league.end_time,
    location: league.location,
    
    // Use real price from database
    registrationFee: realLeague?.entry_fee || 0,
    entry_fee: realLeague?.entry_fee || 0,
    
    // Pass all category information from Supabase
    categories: realLeague?.genderCategories || [realLeague?.gender_category || 'mixta'],
    category: realLeague?.category,
    gender_category: realLeague?.gender_category,
    // For leagues: build genderCategories with tennisCategories fallback
    genderCategories: (() => {
      if (realLeague?.genderCategories?.length > 0) return realLeague.genderCategories;
      // Direct parse from rules
      try {
        const rulesData = typeof realLeague?.rules === 'string' 
          ? JSON.parse(realLeague.rules) : realLeague?.rules;
        const tc = rulesData?.ladderConfig?.tennisCategories;
        if (tc?.selectedGenders?.length > 0) return tc.selectedGenders;
      } catch (e) { /* ignore */ }
      return realLeague?.gender_category ? [realLeague.gender_category] : [];
    })(),
    
    // For leagues: build subcategories with tennisCategories fallback
    subcategories: (() => {
      if (realLeague?.subcategories && Object.keys(realLeague.subcategories).length > 0) 
        return realLeague.subcategories;
      // Direct parse from rules
      try {
        const rulesData = typeof realLeague?.rules === 'string' 
          ? JSON.parse(realLeague.rules) : realLeague?.rules;
        const tc = rulesData?.ladderConfig?.tennisCategories;
        if (tc?.subcategoryDetails && Object.keys(tc.subcategoryDetails).length > 0) {
          const subs: Record<string, string[]> = {};
          Object.entries(tc.subcategoryDetails).forEach(([gender, details]) => {
            if (Array.isArray(details)) subs[gender] = details.map((d: any) => d.name || d);
          });
          return subs;
        }
      } catch (e) { /* ignore */ }
      return realLeague?.gender_category && realLeague?.category 
        ? { [realLeague.gender_category]: [realLeague.category] } : {};
    })(),
    rules: realLeague?.rules,
    
    participants: 0,
    maxParticipants: realLeague?.max_participants || 100,
    matchType: league.partner ? 'dobles' : 'singles' as 'singles' | 'dobles',
    match_type: realLeague?.match_type,
    event_type: realLeague?.event_type || 'league',
    isLeague: true,
    leagueFormat: (league as any).leagueFormat || 'round-robin',
    
    // Format details from Supabase
    group_rules: realLeague?.group_rules,
    additional_rules: realLeague?.additional_rules,
    game_days: realLeague?.game_days || league.game_days,
    address: (realLeague as any)?.address,
    postal_code: (realLeague as any)?.postal_code,
    organizer_id: realLeague?.organizer_id,
    ladderConfig: realLeague?.ladderConfig,
  };

  // DEBUG: Log para ver qué se está pasando al formulario
  console.log('🔍 DEBUG eventForRegistration:', {
    id: eventForRegistration.id,
    title: eventForRegistration.title,
    start_time: eventForRegistration.start_time,
    end_time: eventForRegistration.end_time,
    time: eventForRegistration.time
  });

  return (
    <div className="space-y-6">
      {/* Hero Image with Overlaid Icons */}
      <div className="relative">
        <div className="relative overflow-hidden rounded-lg">
          <img 
            src={getOrganizerImage(league.organizer, league.sport, league.image_url)}
            alt={`${league.title} - ${league.organizer}`}
            className="w-full h-40 md:h-48 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/20" />
          
          {/* Back Icon - Top Left */}
          <button 
            onClick={onBack}
            className="absolute top-4 left-4 w-10 h-10 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full flex items-center justify-center shadow-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-white" />
          </button>
          
          {/* Chat Icon - Top Right */}
          {onContactOrganizer && (
            <button 
              onClick={onContactOrganizer}
              className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full flex items-center justify-center shadow-lg transition-colors"
            >
              <MessageCircle className="h-5 w-5 text-white" />
            </button>
          )}
          
          
          {/* Glass overlay with title and details */}
          <div className="absolute bottom-0 left-0 right-0 bg-black/40 backdrop-blur-md p-3">
            {/* Row 1: League Title and Sport */}
            <div className="flex items-center justify-between text-white mb-1">
              <h1 className="text-base md:text-xl font-bold truncate mr-2">{league.title}</h1>
              <span className="text-xs opacity-90 whitespace-nowrap">{league.sport}</span>
            </div>
            
            {/* Row 2: Location and Date */}
            <div className="flex items-center justify-between text-white text-xs">
              <div className="flex items-center gap-1.5">
                <MapPin className="h-3 w-3 flex-shrink-0" />
                <span className="opacity-90 truncate">{league.location}</span>
              </div>
              <div className="flex items-center gap-1.5 whitespace-nowrap">
                <Calendar className="h-3 w-3 flex-shrink-0" />
                <span>
                  {format(new Date(league.startDate), 'd MMM', { locale: es })} - {format(new Date(league.endDate || league.startDate), 'd MMM yyyy', { locale: es })}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Registration Form Section */}
      {showRegistrationForm && (
        <div id="registration-section">
          <EventRegistrationForm 
            event={eventForRegistration}
            onBack={() => {}}
            onRegister={() => {}}
          />
        </div>
      )}

      {/* League Content */}
      {renderLeagueView()}
    </div>
  );
};

export default LeagueDetails;
